import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from './components/AppShell.js';
import { LoadingState } from './components/ui/LoadingState.js';
import { EmptyState } from './components/ui/EmptyState.js';
import { Button } from './components/ui/Button.js';
import { SearchOverlay } from './components/SearchOverlay.js';
import { LoginPage } from './pages/LoginPage.js';
import { SetPasswordPage } from './pages/SetPasswordPage.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { EmailPage } from './pages/EmailPage.js';
import { LeadsPage } from './pages/LeadsPage.js';
import { LeadDetailPage } from './pages/LeadDetailPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { ShowcasePage } from './pages/ShowcasePage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { TasksPage } from './pages/TasksPage.js';
import { TaskDetailPage } from './pages/TaskDetailPage.js';
import { MessagingTriagePage } from './pages/MessagingTriagePage.js';
import { ProjectsPage } from './pages/ProjectsPage.js';
import { ProjectDetailPage } from './pages/ProjectDetailPage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { PlatformAdminPage } from './pages/PlatformAdminPage.js';
import { api, ApiError } from './lib/api.js';
import { useAllSettings } from './hooks/useSettings.js';
import { useOnboardingProgress, isOnboardingComplete } from './hooks/useOnboarding.js';
import { SessionProvider, type SessionInfo } from './contexts/SessionContext.js';

const AUTH_ME_MAX_ATTEMPTS = 4;
const AUTH_ME_RETRY_DELAYS_MS = [1000, 2000, 4000];

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default function App() {
  const { t, i18n } = useTranslation();
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  // Bug real (2026-08-05): antes, CUALQUIER fallo de /api/auth/me (401 real,
  // 500 transitorio, un hiccup de red) mandaba a la pantalla de login — un
  // 500 puntual no significa que la sesión sea inválida, pero el usuario lo
  // vivía exactamente igual: "se cerró la sesión sola". Ahora: 401 real →
  // logout inmediato (la sesión de verdad no es válida). Cualquier otro
  // error → reintenta con backoff; si se agotan los intentos, se muestra un
  // estado de error explícito con botón de reintentar — nunca la pantalla
  // de login, porque no sabemos que la sesión sea inválida.
  const [authCheckError, setAuthCheckError] = useState<string | null>(null);
  const [session, setSession] = useState<SessionInfo>({ displayName: null, accountName: null, role: null, isPlatformAdmin: false });
  const [showSearch, setShowSearch] = useState(false);
  const { data: settings } = useAllSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: onboardingData } = useOnboardingProgress(authenticated === true);
  // Redirige a /onboarding UNA sola vez por sesión de login — si el cliente
  // navega a otra ruta después, no lo interrumpe de nuevo (skippable, per
  // spec). No dispara si aterriza directo en otra ruta (ej. abrió un link
  // directo a /email) — solo cuando entra por el dashboard default.
  const onboardingRedirectCheckedRef = useRef(false);

  // Sync language from DB settings on startup (DB beats localStorage for cross-session persistence)
  useEffect(() => {
    const lang = settings?.['ui_language'];
    if (lang && lang !== i18n.language) {
      void i18n.changeLanguage(lang);
    }
  }, [settings, i18n]);

  async function fetchSession(): Promise<void> {
    setAuthCheckError(null);
    for (let attempt = 1; attempt <= AUTH_ME_MAX_ATTEMPTS; attempt++) {
      try {
        const d = await api.get<{ authenticated: boolean; displayName?: string | null; accountName?: string | null; role?: 'owner' | 'member' | null; isPlatformAdmin?: boolean }>('/api/auth/me');
        setAuthenticated(d.authenticated);
        if (d.authenticated) {
          setSession({ displayName: d.displayName ?? null, accountName: d.accountName ?? null, role: d.role ?? null, isPlatformAdmin: d.isPlatformAdmin ?? false });
        }
        return;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setAuthenticated(false);
          return;
        }
        const isLastAttempt = attempt === AUTH_ME_MAX_ATTEMPTS;
        if (isLastAttempt) {
          setAuthCheckError(err instanceof Error ? err.message : 'No se pudo confirmar la sesión.');
          return;
        }
        await sleep(AUTH_ME_RETRY_DELAYS_MS[attempt - 1] ?? 4000);
      }
    }
  }

  useEffect(() => {
    void fetchSession();
  }, []);

  useEffect(() => {
    if (!authenticated) { onboardingRedirectCheckedRef.current = false; return; }
    if (onboardingRedirectCheckedRef.current) return;
    if (!onboardingData) return; // esperar a que cargue antes de decidir
    onboardingRedirectCheckedRef.current = true;
    if (location.pathname === '/' && !isOnboardingComplete(onboardingData.progress)) {
      navigate('/onboarding', { replace: true });
    }
  }, [authenticated, onboardingData, location.pathname, navigate]);

  useEffect(() => {
    if (!authenticated) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(s => !s);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [authenticated]);

  if (authCheckError) {
    return (
      <div
        className="flex h-full items-center justify-center"
        style={{ background: 'var(--evo-bg)' }}
      >
        <EmptyState
          title={t('app.authCheckFailed.title', 'No se pudo confirmar tu sesión')}
          description={t('app.authCheckFailed.description', 'Puede ser un problema de conexión — tu sesión probablemente sigue activa.')}
          action={<Button variant="solid" tone="cyan" onClick={() => { void fetchSession(); }}>{t('app.authCheckFailed.retryBtn', 'Reintentar')}</Button>}
        />
      </div>
    );
  }

  if (authenticated === null) {
    return (
      <div
        className="flex h-full items-center justify-center"
        style={{ background: 'var(--evo-bg)' }}
      >
        <LoadingState fullHeight />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <Routes>
        <Route path="/set-password" element={<SetPasswordPage onSuccess={() => { void fetchSession(); }} />} />
        <Route path="*" element={<LoginPage onSuccess={() => { void fetchSession(); }} />} />
      </Routes>
    );
  }

  return (
    <SessionProvider value={session}>
      <AppShell>
        {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route
            path="/admin/signup-requests"
            element={session.isPlatformAdmin ? <PlatformAdminPage /> : <Navigate to="/" replace />}
          />
          <Route path="/email" element={<EmailPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/leads/:id" element={<LeadDetailPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/:id" element={<ReportsPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/:id" element={<TaskDetailPage />} />
          <Route path="/mensajes/triage" element={<MessagingTriagePage />} />
          <Route path="/facturacion" element={<BillingPage />} />
          <Route path="/facturacion/:type/:id" element={<BillingPage />} />
          <Route path="/showcase" element={<ShowcasePage />} />
          <Route path="/proyectos" element={<ProjectsPage />} />
          <Route path="/proyectos/:slug" element={<ProjectDetailPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </SessionProvider>
  );
}
